import React, { useState } from "react";
import { Alert, Button, Input, Tooltip, Typography } from "antd";
import {
  DislikeFilled,
  DislikeOutlined,
  LikeFilled,
  LikeOutlined,
  ReloadOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import type {
  DataQuestionFeedback,
  DataQuestionTurn,
} from "../../store/api/dataQuestionsApi";
import { MarkdownLite } from "./markdownLite";
import { ResultTable } from "./ResultTable";
import { TraceSteps } from "./TraceSteps";
import styles from "./styles.module.css";

interface TurnCardProps {
  turn: DataQuestionTurn;
  links: Record<string, string>;
  disabled?: boolean;
  onFollowUp: (question: string) => void;
  onFeedback: (turnId: number, feedback: DataQuestionFeedback) => void;
  /** Rejects when the save failed, so the name input stays open. */
  onSave: (turnId: number, name: string) => Promise<void>;
  onUnsave: (turnId: number) => Promise<void>;
  onRerun: (turnId: number) => Promise<void>;
  rerunning?: boolean;
}

/** One question and its answer: trace, text, assumptions, table, follow-ups, feedback. */
export const TurnCard: React.FC<TurnCardProps> = ({
  turn,
  links,
  disabled = false,
  onFollowUp,
  onFeedback,
  onSave,
  onUnsave,
  onRerun,
  rerunning = false,
}) => {
  const [saving, setSaving] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveBusy, setSaveBusy] = useState(false);

  const submitSave = async () => {
    const name = saveName.trim();
    if (!name) return;
    setSaveBusy(true);
    try {
      await onSave(turn.turnId, name);
      setSaving(false);
      setSaveName("");
    } catch {
      // The page already showed the error; keep the typed name so the admin
      // can retry without retyping.
    } finally {
      setSaveBusy(false);
    }
  };

  return (
    <article className={styles.turn} data-testid={`turn-${turn.turnId}`}>
      <div className={styles.question}>{turn.question}</div>
      <div className={styles.assistant}>
        {turn.steps.length > 0 && <TraceSteps steps={turn.steps} />}

        {turn.status === "ERROR" && (
          <Alert
            type="error"
            showIcon
            message="No pude responder esa pregunta"
            description={turn.error ?? "Probá reformularla o acotarla."}
          />
        )}
        {turn.status === "CLARIFY" && turn.answer && (
          <Alert type="info" showIcon message={turn.answer} />
        )}
        {turn.status === "OK" && turn.answer && (
          <MarkdownLite className={styles.answer} text={turn.answer} />
        )}

        {turn.assumptions.length > 0 && (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Supuestos
            <ul className={styles.assumptions}>
              {turn.assumptions.map((assumption, index) => (
                <li key={index}>{assumption}</li>
              ))}
            </ul>
          </Typography.Paragraph>
        )}

        {turn.result ? (
          <ResultTable
            result={turn.result}
            links={links}
            sql={turn.sql}
            title={turn.savedName ?? turn.question}
          />
        ) : (
          turn.sql && (
            <div>
              <Button
                icon={<ReloadOutlined />}
                loading={rerunning}
                onClick={() => void onRerun(turn.turnId)}
              >
                Volver a ejecutar la consulta
              </Button>
            </div>
          )
        )}

        {turn.followUps.length > 0 && (
          <div className={styles.followUps}>
            {turn.followUps.map((followUp) => (
              <Button
                key={followUp}
                size="small"
                type="dashed"
                disabled={disabled}
                onClick={() => onFollowUp(followUp)}
              >
                {followUp}
              </Button>
            ))}
          </div>
        )}

        <div className={styles.turnFooter}>
          <Tooltip title="Respuesta correcta">
            <Button
              size="small"
              type="text"
              aria-label="Respuesta correcta"
              aria-pressed={turn.feedback === "UP"}
              icon={turn.feedback === "UP" ? <LikeFilled /> : <LikeOutlined />}
              onClick={() => onFeedback(turn.turnId, "UP")}
            />
          </Tooltip>
          <Tooltip title="Respuesta incorrecta o incompleta">
            <Button
              size="small"
              type="text"
              aria-label="Respuesta incorrecta"
              aria-pressed={turn.feedback === "DOWN"}
              icon={turn.feedback === "DOWN" ? <DislikeFilled /> : <DislikeOutlined />}
              onClick={() => onFeedback(turn.turnId, "DOWN")}
            />
          </Tooltip>
          {turn.sql &&
            (turn.savedName ? (
              <>
                <Typography.Text type="secondary">
                  Guardada como <Typography.Text strong>{turn.savedName}</Typography.Text>
                </Typography.Text>
                <Button size="small" type="link" onClick={() => void onUnsave(turn.turnId)}>
                  Quitar
                </Button>
              </>
            ) : saving ? (
              <span className={styles.saveRow}>
                <Input
                  size="small"
                  autoFocus
                  placeholder="Nombre de la consulta"
                  aria-label="Nombre de la consulta"
                  value={saveName}
                  maxLength={256}
                  onChange={(event) => setSaveName(event.target.value)}
                  onPressEnter={() => void submitSave()}
                  style={{ width: 240 }}
                />
                <Button size="small" type="primary" loading={saveBusy} onClick={() => void submitSave()}>
                  Guardar
                </Button>
                <Button size="small" type="text" onClick={() => setSaving(false)}>
                  Cancelar
                </Button>
              </span>
            ) : (
              <Button size="small" type="text" icon={<SaveOutlined />} onClick={() => setSaving(true)}>
                Guardar consulta
              </Button>
            ))}
          <span className={styles.spacer} />
          {turn.durationMs > 0 && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {(turn.durationMs / 1000).toFixed(1)} s
            </Typography.Text>
          )}
        </div>
      </div>
    </article>
  );
};
